import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ObjectId } from 'bson';
import {
  useFinancingLenderExpressInterestApi,
  useFinancingLenderMarkFundedApi,
  useFinancingLenderMarkRepaidApi,
  useFinancingLenderRejectRepaymentApi,
  useFinancingLenderSubmitOfferApi,
  useFinancingLenderViewApi,
} from '@hero/api-hooks';
import type { FinancingDeclineReason, FinancingOfferTerms } from '@hero/schema';
import { LenderViewHero } from './LenderViewHero';
import { LenderViewDetail } from './LenderViewDetail';
import { LenderViewActions } from './LenderViewActions';
import {
  LenderFundingPanel,
  LenderFundingRecordedCard,
} from './LenderFundingPanel';
import {
  LenderRepaymentPanel,
  LenderRepaymentRecordedCard,
  LenderRepaymentRejectionBanner,
} from './LenderRepaymentPanel';

/**
 * Public, unauthenticated lender-view of a single financing
 * application at `/financing-application/:appId?i=<lenderId>`.
 *
 * v0 trust model (pilot): the `?i=` value is the canonical hex
 * `Lender._id` from the admin-managed `Lender` directory; the
 * backend matches it against `offers[i].lender._id`. No token,
 * no JWT. The `getLenderViewApplicationAPI` endpoint returns
 * `financing_partner_invite_not_found` for any unknown
 * `(appId, lenderId)` pair so a guessed `?i=` cannot probe for
 * existence.
 *
 * Single network call: the parent `useFinancingLenderViewApi`
 * query returns `{ application, org, lender, client?, agreement? }`
 * in one shot. The previous two-call shape (partner application
 * + active-lenders list) is gone, which is what makes the page
 * survive a hard refresh without the lender-branding flickering.
 * TanStack's persisted cache (`PersistQueryClientProvider`)
 * additionally rehydrates the prior response instantly so the
 * Tarmeez-style banner stays stable.
 *
 * The three CTAs (interest / submit-offer / decline) route through
 * matching api-hook mutations that all return the same
 * `FinancingLenderView` shape, letting TanStack stamp the new
 * decision into the same cache entry without a refetch.
 *
 * See `docs/financing/partner-access.md` for the trust model and
 * `docs/financing/roadmap.md` for the v1 authed migration path.
 */
export default function FinancingLenderViewPage() {
  const { t } = useTranslation('m-financing');
  const params = useParams<{ appId: string }>();
  const [searchParams] = useSearchParams();
  const appId = params.appId ?? '';
  /**
   * `?i=` (lender id) is the canonical query param. Legacy invite
   * emails carry `?p=` instead; we read both so the back-compat
   * redirect from `/financing/partner/:appId?p=` works even if a
   * client somehow lands here with the old query name.
   */
  const lenderId = searchParams.get('i') ?? searchParams.get('p') ?? '';

  const viewQuery = useFinancingLenderViewApi({
    _id: appId ? new ObjectId(appId) : (undefined as unknown as ObjectId),
    i: lenderId,
  });

  const expressInterest = useFinancingLenderExpressInterestApi();
  const submitOffer = useFinancingLenderSubmitOfferApi();
  const markFunded = useFinancingLenderMarkFundedApi();
  const markRepaid = useFinancingLenderMarkRepaidApi();
  const rejectRepayment = useFinancingLenderRejectRepaymentApi();

  if (!lenderId || !appId) {
    return (
      <ErrorState
        title={t('partner_page.errors.not_found_title')}
        description={t('partner_page.errors.missing_partner_id')}
      />
    );
  }

  if (viewQuery.isLoading && !viewQuery.data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto w-full max-w-6xl space-y-4 px-4 py-6 sm:px-6 sm:py-8">
          <div className="h-24 animate-pulse rounded-2xl bg-white shadow-sm" />
          <div className="h-48 animate-pulse rounded-2xl bg-white shadow-sm" />
        </div>
      </div>
    );
  }

  const view = viewQuery.data;
  if (!view) {
    return (
      <ErrorState
        title={t('partner_page.errors.not_found_title')}
        description={t('partner_page.errors.not_found_description')}
      />
    );
  }

  const handleInterested = async () => {
    try {
      await expressInterest.mutateAsync({
        _id: view.application._id,
        i: lenderId,
      });
      toast.success(t('partner_page.toasts.interest_success'));
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('partner_page.toasts.interest_failed'),
      );
    }
  };

  const handleSubmitOffer = async (terms: FinancingOfferTerms) => {
    await submitOffer.mutateAsync({
      _id: view.application._id,
      i: lenderId,
      decision: 'offered',
      terms,
    });
  };

  const handleDecline = async (input: {
    reason: FinancingDeclineReason;
    notes?: string;
  }) => {
    await submitOffer.mutateAsync({
      _id: view.application._id,
      i: lenderId,
      decision: 'declined',
      declineReason: input.reason,
      declineNotes: input.notes,
    });
  };

  /**
   * Lender stamps the operational funding event. The backend gates
   * this on `(offer.decision === 'accepted', app.status === 'accepted')`
   * — the same gate the panel's visibility check uses below — and
   * returns the refreshed lender-view shape so TanStack persists the
   * new `status: 'funded'` in cache. Errors are surfaced through the
   * panel's own toast handler (the mutation promise re-throws).
   */
  const handleConfirmFunding = async (input: {
    at: Date;
    fundingProof: { upload: string };
    notes?: string;
  }) => {
    await markFunded.mutateAsync({
      _id: view.application._id,
      i: lenderId,
      at: input.at,
      fundingProof: input.fundingProof,
      notes: input.notes,
    });
  };

  /**
   * Lender stamps the operational repayment event. Two valid entry
   * paths — both flow through this single handler so the panel can
   * stay agnostic of which mode is rendering it:
   *
   *  - **Approval path** — `status: 'awaiting_repayment_approval'`,
   *    `repaymentProof` is omitted by the panel. The backend reuses
   *    `application.repaymentReceipt.image` as the canonical proof.
   *  - **Direct-record path** — `status: 'funded'`, `repaymentProof`
   *    carries a fresh upload from the lender's file picker.
   *
   * Backend gates on `offer.decision === 'accepted'` and the status
   * being one of `funded` / `awaiting_repayment_approval`. Returns
   * the refreshed lender-view shape so TanStack persists the new
   * `status: 'repaid'` in cache. Errors are surfaced through the
   * panel's own toast handler (the mutation promise re-throws).
   */
  const handleConfirmRepayment = async (input: {
    at: Date;
    repaymentProof?: { upload: string };
    notes?: string;
  }) => {
    await markRepaid.mutateAsync({
      _id: view.application._id,
      i: lenderId,
      at: input.at,
      repaymentProof: input.repaymentProof,
      notes: input.notes,
    });
  };

  /**
   * Lender rejects the carrier's submitted repayment receipt with a
   * required reason. Status reverts to `funded`, the receipt is
   * cleared, and the reason lands on `outcome.notes` so the carrier
   * sees it on retry. Returns the refreshed lender-view shape, so
   * TanStack swaps the review panel for the direct-record panel
   * (plus the rejection banner) without a second refetch.
   */
  const handleRejectRepayment = async (input: { notes: string }) => {
    await rejectRepayment.mutateAsync({
      _id: view.application._id,
      i: lenderId,
      notes: input.notes,
    });
  };

  /**
   * Post-acceptance surface choice:
   *  - `decision: 'accepted'` + `app.status: 'accepted'` → the
   *    lender hasn't funded yet. Render the funding panel and skip
   *    the generic accepted-state banner (the panel itself carries
   *    the "carrier picked you" header).
   *  - `decision: 'accepted'` + `app.status: 'funded'` → the lender
   *    has funded but the carrier hasn't submitted a receipt yet.
   *    Render the repayment panel in "direct-record" mode (lender
   *    closes the loop with their own proof). Also show the
   *    rejection banner if `lastRejectionNotes` is set so the
   *    lender knows why they bounced back to this state.
   *  - `decision: 'accepted'` + `app.status: 'awaiting_repayment_approval'`
   *    → the carrier submitted a receipt; render the repayment
   *    panel in "review" mode so the lender can approve / reject.
   *  - `decision: 'accepted'` + `app.status: 'repaid'` → the loop
   *    closed. Render the read-only repayment receipt card.
   *  - Any other combination → fall through to `LenderViewActions`
   *    which handles `pending` / `interested` / `offered` /
   *    `declined`.
   */
  const isAccepted = view.application.myOffer.decision === 'accepted';
  const isAppAccepted =
    isAccepted && view.application.status === 'accepted';
  const isAppFunded = isAccepted && view.application.status === 'funded';
  const isAppAwaitingReview =
    isAccepted && view.application.status === 'awaiting_repayment_approval';
  const isAppRepaid = isAccepted && view.application.status === 'repaid';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <LenderViewHero
          lender={view.lender}
          carrierName={view.org.name}
          refValue={view.application.ref}
        />

        <LenderViewDetail view={view} />

        {isAppAccepted ? (
          <LenderFundingPanel
            view={view}
            onConfirmFunding={handleConfirmFunding}
          />
        ) : isAppFunded ? (
          <div className="space-y-4">
            <LenderFundingRecordedCard view={view} />
            <LenderRepaymentRejectionBanner view={view} />
            <LenderRepaymentPanel
              view={view}
              onConfirmRepayment={handleConfirmRepayment}
              onRejectRepayment={handleRejectRepayment}
            />
          </div>
        ) : isAppAwaitingReview ? (
          <div className="space-y-4">
            <LenderFundingRecordedCard view={view} />
            <LenderRepaymentPanel
              view={view}
              onConfirmRepayment={handleConfirmRepayment}
              onRejectRepayment={handleRejectRepayment}
            />
          </div>
        ) : isAppRepaid ? (
          <LenderRepaymentRecordedCard view={view} />
        ) : (
          <LenderViewActions
            view={view}
            onExpressInterest={handleInterested}
            onSubmitOffer={handleSubmitOffer}
            onDecline={handleDecline}
          />
        )}
      </div>
    </div>
  );
}

function ErrorState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto w-full max-w-2xl space-y-3 px-4 py-12 text-center sm:px-6">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );
}
